import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const STAGE_LABELS = { none: "No stage", assess: "Assess", stabilise: "Stabilise", decide: "Decide", sustain: "Sustain" };
const STAGE_TONE = { none: "bg-secondary text-muted-foreground border-border", assess: "bg-info-100 text-info-900 border-info-200", stabilise: "bg-warning-100 text-warning-900 border-warning-200", decide: "bg-highlight-100 text-highlight-900 border-highlight-200", sustain: "bg-success-100 text-success-900 border-success-200" };

// Manual, audited stage transitions. Skipping is allowed; leaving Assess after its end date is blocked server-side unless a decision exists or the date is extended.
export default function StageControl({ change, onChanged }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const stage = change.stage || "none";
  const writer = can("write") && !change.is_example;
  const move = async (next) => {
    setOpen(false);
    if (next === stage) return;
    setBusy(true);
    try { await api.post(`/changes/${change.id}/stage`, { stage: next }); toast.success(next === "none" ? "Stage cleared" : `Moved to ${STAGE_LABELS[next]}`); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!writer) return <span data-testid="stage-badge" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${STAGE_TONE[stage]}`}>{STAGE_LABELS[stage]}</span>;
  return (
    <div className="relative inline-block">
      <button data-testid="stage-control" onClick={() => setOpen(!open)} disabled={busy} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold transition-colors hover:brightness-95 ${STAGE_TONE[stage]}`} aria-haspopup="menu" aria-expanded={open}>
        Stage: {STAGE_LABELS[stage]} <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" aria-label="Close stage menu" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute left-0 top-full mt-1 z-20 w-44 rounded-xl border border-border bg-card shadow-xl p-1" data-testid="stage-menu">
            {["none", "assess", "stabilise", "decide", "sustain"].map((s) => (
              <button key={s} role="menuitem" data-testid={`stage-opt-${s}`} onClick={() => move(s)} className={`w-full text-left rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${s === stage ? "bg-secondary text-foreground" : "text-subtle hover:bg-muted"}`}>{STAGE_LABELS[s]}{s === stage && <span className="text-[10px] text-muted-foreground"> · current</span>}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
